import { useTranslation } from "@/hooks/useTranslation";
import {
  ChevronLeftIcon,
  MessageCircleIcon,
  MoreHorizontalIcon,
} from "lucide-react";
import { Button } from "../ui/button";
import { useStandaloneMode } from "@/hooks/useStandaloneMode";
import { FavoriteButton } from "./FavoriteButton";
import { useLocation, useNavigate } from "react-router-dom";
import { getIOSVersion } from "@/lib/utils";

interface LocationState {
  from?: string;
}

export default function ProfileSkeletonLoader() {
  const isStandalone = useStandaloneMode();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslation();
  const iosVersion = getIOSVersion();

  const handleBackClick = () => {
    const state = location.state as LocationState;
    if (state && state.from) {
      navigate(state.from);
    } else {
      navigate(-1);
    }
  };

  function NameAndLastSeenSkeleton() {
    return (
      <div className="w-full flex flex-wrap gap-y-2 items-center justify-between">
        <div className="w-40 h-8 rounded-full bg-zinc-800 animate-pulse" />
        <div className="w-36 h-8 rounded-full bg-zinc-800 animate-pulse" />
      </div>
    );
  }

  function BadgesSkeleton() {
    return (
      <div className="flex flex-wrap w-full gap-x-3 gap-y-4">
        <div className="w-32 h-8 rounded-full bg-zinc-800 animate-pulse" />
        <div className="w-40 h-8 rounded-full bg-zinc-800 animate-pulse" />
      </div>
    );
  }

  function BioSkeleton() {
    return (
      <div className="w-full h-24 rounded-2xl bg-zinc-800 animate-pulse" />
    );
  }

  function TabBarSkeleton() {
    const detachingBarIosVersions = ["26.0"];
    const versionString = iosVersion
      ? `${iosVersion.major}.${iosVersion.minor}`
      : null;

    if (versionString && detachingBarIosVersions.includes(versionString)) {
      return (
        <div
          className={`md:hidden min-w-[320px] w-[50vw] !fixed ${
            isStandalone ? "!bottom-7" : "!bottom-3"
          } inset-x-0 mx-auto bg-white/20 backdrop-blur-md px-4 z-20 pointer-events-auto rounded-full border border-zinc-100/20`}
        >
          <div className="flex flex-row justify-between items-center py-1">
            <FavoriteButton active={false} userId={""} />
            <Button
              variant="ghost"
              className="rounded-full hover:bg-transparent"
            >
              <MessageCircleIcon
                strokeWidth={0}
                className="h-9 w-9 mr-2 fill-white/90"
              />
              <span className="text-white/90">
                {t.PROFILE_VIEW.LABELS.MESSAGE_BUTTON}
              </span>
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 bg-black px-6 border-t-[#303030] border-t z-20 pb-[calc(env(safe-area-inset-bottom)+0.25rem)]"
      >
        <div className="flex flex-row justify-between items-center py-3">
          <FavoriteButton active={false} userId={""} />
          <Button variant="secondary" className="mr-4 rounded-full py-6">
            <MessageCircleIcon className="h-6 w-6 mr-2 fill-black" />
            {t.PROFILE_VIEW.LABELS.MESSAGE_BUTTON}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full max-w-5xl mx-auto md:mt-4">
      <div className="flex flex-row justify-between items-center mb-2 md:mb-4 pl-1">
        <Button variant="default" onClick={handleBackClick}>
          <ChevronLeftIcon className="mr-2 h-4 w-4" />
          {t.SHARED.LABELS.BACK_BUTTON}
        </Button>

        <div className="flex flex-row items-center gap-2">
          <Button variant="default" className="px-3">
            <MoreHorizontalIcon className="h-6 w-6" />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:px-0">
        <div className="aspect-[3/4] bg-zinc-800 -mx-2 md:mx-0 md:rounded-lg animate-pulse" />
        <div className="w-full px-4 md:h-3/4 md:bg-zinc-800 md:rounded-lg md:animate-pulse mb-20">
          <div className="w-full flex flex-col gap-y-6">
            <NameAndLastSeenSkeleton />
            <BadgesSkeleton />
            <BioSkeleton />
            <BadgesSkeleton />
          </div>
        </div>
      </div>

      <TabBarSkeleton />
    </div>
  );
}
